import React from "react";
import { formatCurrency } from "../../../helpers/estimateTotals";
import { REPORT_HEADER_SHELL } from "../../../helpers/reportHeader";
import { EstimateReportTotalSection } from "./EstimateReportTotalSection";
import { EstimateReportSummarySection } from "./EstimateReportSummarySection";
import { ReportPreviewHeader } from "../../report/ReportPreviewHeader";
import { ReportRule } from "../../report/ReportRule";
import { ReportItemRows } from "../../report/ReportItemRows";
import { ReportSubtotalRow } from "../../report/ReportSubtotalRow";
import { ReportNotesSection } from "../../report/ReportNotesSection";
import "../../report/ReportPreviewDocument.css";
import "./EstimateReportDocument.css";

function LineList({ items }) {
  if (!items?.length) return null;

  return (
    <ul className="er-line-list">
      {items.map((item, index) => (
        <li key={`${item}-${index}`}>{item}</li>
      ))}
    </ul>
  );
}

function WorkTypeSection({ workType, showPricingDetails }) {
  const hasMeasurements = workType.measurements?.length > 0;
  const hasMaterials = workType.materials?.length > 0;
  const hasLabour = workType.labour?.length > 0;

  return (
    <div className="er-work-type-block">
      <h4 className="er-work-type-name">{workType.label}</h4>

      {hasMeasurements && (
        <>
          <div className="er-subsection-title">Measurements</div>
          <LineList items={workType.measurements} />
        </>
      )}

      {hasMaterials && (
        <>
          <div className="er-subsection-title">Materials</div>
          <ReportItemRows rows={workType.materials} variant="preview" />
        </>
      )}

      {hasLabour && (
        <>
          <div className="er-subsection-title">Labour</div>
          <ReportItemRows rows={workType.labour} variant="preview" />
        </>
      )}

      {showPricingDetails && workType.total != null && workType.total > 0 && (
        <ReportSubtotalRow
          label="Work Type Total"
          amount={workType.total}
          variant="preview"
        />
      )}
    </div>
  );
}

function LocationSection({ location, showPricingDetails }) {
  return (
    <div className="er-location-block">
      <h3 className="er-location-name">{location.name}</h3>
      <ReportRule thin variant="preview" />

      {location.workTypes.map((workType) => (
        <WorkTypeSection
          key={workType.id}
          workType={workType}
          showPricingDetails={showPricingDetails}
        />
      ))}

      {showPricingDetails && location.total != null && location.total > 0 && (
        <div className="er-location-total">
          <span>Location Total</span>
          <span>{formatCurrency(location.total)}</span>
        </div>
      )}
    </div>
  );
}

/**
 * On-screen estimate report preview — mobile/tablet review layout.
 * PDF export uses EstimateReportPrintDocument with the same reportModel.
 */
export function EstimateReportDocument({ reportModel }) {
  if (!reportModel) return null;

  const { header, summary, scopeOverview, locations, notes, options } =
    reportModel;
  const showPricingDetails = options.showPricingDetails;
  const showEstimateTotal = options.showEstimateTotal;
  const estimateTotalPresentation =
    showEstimateTotal && reportModel.grandTotal != null;

  return (
    <article className="report-preview-document" aria-label="Estimate Report">
      <ReportPreviewHeader
        {...REPORT_HEADER_SHELL.estimate}
        fields={header.fields}
      />

      {summary && <EstimateReportSummarySection summary={summary} />}

      {scopeOverview.length > 0 && (
        <>
          <ReportRule variant="preview" />
          <h2 className="er-section-title">Project Scope</h2>
          {scopeOverview.map((location) => (
            <div key={location.name} className="er-scope-location">
              <div className="er-scope-location-name">{location.name}</div>
              {location.workTypes.length > 0 ? (
                <ul className="er-scope-list">
                  {location.workTypes.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
              ) : (
                <div className="er-empty-hint">No work types</div>
              )}
            </div>
          ))}
        </>
      )}

      {locations.length > 0 && (
        <>
          <ReportRule variant="preview" />
          <h2 className="er-section-title">Detailed Breakdown</h2>
          {locations.map((location) => (
            <LocationSection
              key={location.id}
              location={location}
              showPricingDetails={showPricingDetails}
            />
          ))}
        </>
      )}

      {estimateTotalPresentation && (
        <>
          <ReportRule variant="preview" />
          <div className="er-totals-wrap">
            <EstimateReportTotalSection reportModel={reportModel} variant="preview" />
          </div>
        </>
      )}

      {notes && <ReportNotesSection notes={notes} variant="preview" />}
    </article>
  );
}
